import React, { FC, ReactNode, useMemo, useState } from "react";
import { Typography } from "@mui/material";
import TranslationWrapper from "../../old/beheer/components/TranslationWrapper/TranslationWrapper.tsx";
import { TimeTable, TimeTableExtensionProps } from "./TimeTable.tsx";
import { OpeningHoursActionsMenu } from "./ActionMenus/OpeningHoursActionsMenu.tsx";
import { AddEditExceptionDialog } from "./AddEditExceptionDialog.tsx";
import Button from "@mui/material/Button";
import { ExceptionsTableActionMenu } from "./ActionMenus/ExceptionsTableActionMenu.tsx";
import Box from "@mui/material/Box";
import {
  Day,
  OpeningHours,
  OpeningHoursExceptions,
  OpeningHoursForDay,
  TimeMethods,
  TimeRangeMethods,
  toTimeRange,
} from "@jamezz/react-components";
import _ from "lodash";
import { isAfter, parse, subDays } from "date-fns";
import { v7 } from "uuid";

type OpeningHoursProps<T> = {
  openingHours: OpeningHours<T>;
  onChange: (openingHours: OpeningHours2<T>) => void;
  showExceptions?: boolean;
  ExtensionComponent?: FC<TimeTableExtensionProps<T>>;
  emptyExtensionData: T;
  closedLabel?: ReactNode;
  open24Label?: ReactNode;
};

function parseOutgoingOpeningHours<T>(openingHours: OpeningHours2<T>): OpeningHours2<T> {
  const newOpeningHours = _.cloneDeep(openingHours);
  Object.keys(newOpeningHours).forEach((dayOrDate) => {
    if (dayOrDate !== "exceptions") {
      newOpeningHours[dayOrDate as Day] = newOpeningHours[dayOrDate as Day]?.map((openingHoursForDay) => {
        const timeRange = toTimeRange(openingHoursForDay.hours);
        if (TimeMethods.isSame({ hours: 0, minutes: 0 }, timeRange.end)) {
          timeRange.end.hours = 24;
        }

        return { ...openingHoursForDay, hours: TimeRangeMethods.timeRangeToString(timeRange) };
      });
    }
  });

  return newOpeningHours;
}

export type OpeningHoursForDay2<T> = {
  id: string;
  hours: string;
  data: T;
};
export type OpeningHoursExceptions2<T> = {
  [date: string]: OpeningHoursForDay2<T>[] | undefined;
};
export type OpeningHours2<T> = { [day in Day]?: OpeningHoursForDay2<T>[] } & {
  exceptions?: OpeningHoursExceptions2<T>;
  timezone?: string;
};

function parseIncomingOpeningHours<T>(openingHours: OpeningHours<T>, defaultData: T): OpeningHours2<T> {
  const newOpeningHours: OpeningHours2<T> = _.mapValues(_.cloneDeep(openingHours), (value, key) => {
    if (key === "timezone") {
      return value;
    } else if (key === "exceptions") {
      return _.mapValues(value as OpeningHoursExceptions<T>, (value) =>
        _.map(value as OpeningHoursForDay<T>[], (openingHoursForDay): OpeningHoursForDay2<T> => {
          return typeof openingHoursForDay == "string"
            ? { id: v7(), hours: openingHoursForDay, data: defaultData }
            : { id: v7(), ...openingHoursForDay };
        })
      );
    } else {
      return _.map(value as OpeningHoursForDay<T>[], (openingHoursForDay): OpeningHoursForDay2<T> => {
        return typeof openingHoursForDay == "string"
          ? { id: v7(), hours: openingHoursForDay, data: defaultData }
          : { id: v7(), ...openingHoursForDay };
      });
    }
  }) as OpeningHours2<T>; // TODO NOT OKE!!!!!!!!

  newOpeningHours.exceptions = _.pickBy(newOpeningHours.exceptions ?? {}, (value, key) => {
    const date = parse(key, "yyyy-MM-dd", new Date());
    return isNaN(date.getTime()) || isAfter(date, subDays(new Date(), 14));
  });

  return newOpeningHours;
}

export const OpeningHoursSettings = <T,>({
  openingHours,
  onChange,
  showExceptions = true,
  ExtensionComponent,
  emptyExtensionData,
  closedLabel,
  open24Label,
}: OpeningHoursProps<T>) => {
  const parsedOpeningHours = useMemo(
    () => parseIncomingOpeningHours(openingHours, emptyExtensionData),
    [openingHours, emptyExtensionData]
  );
  // Timezone must not exist in dailyOpeningHours
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { exceptions = {}, timezone, ...dailyOpeningHours } = parsedOpeningHours;
  const [isAddExceptionDialogOpen, setIsAddExceptionDialogOpen] = useState(false);
  return (
    <>
      <Box>
        <TimeTable
          timeTableType={"days"}
          openingHours={dailyOpeningHours}
          onChange={(openingHoursDays) =>
            onChange(parseOutgoingOpeningHours({ ...parsedOpeningHours, ...openingHoursDays }))
          }
          ActionMenu={OpeningHoursActionsMenu<T>}
          ExtensionComponent={ExtensionComponent}
          emptyExtensionData={emptyExtensionData}
          open24Label={open24Label}
          closedLabel={closedLabel}
        />
      </Box>
      {showExceptions ? (
        <>
          <Box sx={{ mt: 4 }}>
            <Typography component={"h5"}>
              <TranslationWrapper tKey={"Exceptions of schema"} tDefault={"Exceptions of schema"} />
            </Typography>
            <Button variant={"contained"} onClick={() => setIsAddExceptionDialogOpen(true)}>
              <TranslationWrapper tKey={"Add exception"} tDefault={"Add exception"} />
            </Button>
            {exceptions ? (
              <TimeTable
                timeTableType={"exceptions"}
                openingHours={exceptions}
                onChange={(exceptions) => {
                  onChange(parseOutgoingOpeningHours({ ...parsedOpeningHours, exceptions: exceptions }));
                }}
                ActionMenu={ExceptionsTableActionMenu<T>}
                ExtensionComponent={ExtensionComponent}
                emptyExtensionData={emptyExtensionData}
                open24Label={open24Label}
                closedLabel={closedLabel}
              />
            ) : null}
          </Box>
          <AddEditExceptionDialog<T>
            open={isAddExceptionDialogOpen}
            defaultData={emptyExtensionData}
            onSave={(date, timeRanges) => {
              const newExceptions = { ...exceptions, [date]: timeRanges };
              onChange(parseOutgoingOpeningHours({ ...parsedOpeningHours, exceptions: newExceptions }));
              setIsAddExceptionDialogOpen(false);
            }}
            onClose={() => setIsAddExceptionDialogOpen(false)}
            ExtensionComponent={ExtensionComponent}
          />
        </>
      ) : null}
    </>
  );
};
